/**
 * MobiliCalendar — the prototype replacement engine (docs/calendar-drop-in-
 * replacement-plan.md). Custom-built day/resource grid designed for 60+
 * columns:
 *
 *  - Grid lines are CSS gradients (zero cell DOM), columns/events absolute.
 *  - Data → indexes (O(1) lookups) built once per snapshot, off the render
 *    hot path. Interaction state lives in a mini external store, so pointer
 *    & keyboard activity re-renders only tiny overlay components.
 *  - Column width is a CSS variable; resizes never re-render React.
 *  - Highlight tiers (patient / cursor / selection / jump pulse) are derived
 *    state per the plan; the jump target is declarative & pending-safe.
 */
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { CalendarEvent, CalendarResource } from "../Calendar/controller/adapters/types";
import {
  DEFAULT_GRID_CONFIG,
  EMPTY_ID_SET,
  MCEvent,
  MCEventMove,
  MCExternalDrop,
  MCGridConfig,
  MCInteractionState,
  MCStats,
  MCTimeRange,
} from "./model/types";
import { createMiniStore } from "./model/miniStore";
import {
  MCEventIndex,
  buildEventIndex,
  backgroundForResource,
  eventsForResource,
  normalizeResources,
} from "./model/eventIndex";
import { cellForPoint, isMovableEvent } from "./model/selection";
import {
  MCPendingMove,
  applyPendingMoves,
  buildPendingMoves,
  satisfiedPendingKeys,
} from "./model/pendingMoves";
import { clampSlot, slotCount, yForMin } from "./model/gridGeometry";
import HeaderCell from "./HeaderCell";
import ResourceColumn from "./ResourceColumn";
import TimeGutter from "./TimeGutter";
import FocusOverlay from "./overlays/FocusOverlay";
import NowLine from "./overlays/NowLine";
import SelectionOverlay from "./overlays/SelectionOverlay";
import ExternalDragOverlay from "./overlays/ExternalDragOverlay";
// EXPERIMENTAL grouping bars — flagged, encapsulated (plan §"Grouping bars").
import GroupingBars from "./overlays/GroupingBars";
import PatientBlockedBand from "./overlays/PatientBlockedBand";
import { isGroupingBarsEnabled } from "./model/groupingBars";
import { useGridKeyboard } from "./useGridKeyboard";
import { useExternalDrag } from "./useExternalDrag";
import { isModalOrDialogOpen } from "../../hotkeys/isTyping";
import "./MobiliCalendar.css";

/**
 * Imperative surface for the bridge's provider-registered hotkeys
 * (useMobiliCalendarHotkeys): lets b/a/n/r/p/… read the keyboard cursor
 * without threading interaction state up through React.
 */
export interface MobiliCalendarKeyboardApi {
  /** Focused cell (resource id + minutes since midnight), null when none. */
  getFocusedCell: () => { resourceId: string; slotMin: number } | null;
  /** Event under the keyboard cursor, null when the cursor is on empty grid. */
  getFocusedEvent: () => MCEvent | null;
}

export interface MobiliCalendarProps {
  events: CalendarEvent[];
  resources: CalendarResource[];
  date: Date;
  isLoading?: boolean;
  /** Tier 1 highlight: all appointments of this patient */
  selectedPatientId?: number | null;
  /** Tier 3: declarative jump target (appointment id); may arrive before data */
  targetAppointmentId?: string | null;
  onJumpTargetHandled?: () => void;
  onEventSelect?: (event: MCEvent) => void;
  /** Keyboard cursor entered/left an event (once per change; consumer debounces). */
  onEventCursor?: (event: MCEvent | null) => void;
  /**
   * Persist a move (keyboard "verschieben", pointer drag, grouping-bar drag)
   * — one entry per picked event. May return a promise; its settlement
   * retires the engine's visual continuity patch (so an error rollback snaps
   * the event back the moment the cache is restored).
   */
  onMoveEvents?: (moves: MCEventMove[]) => void | Promise<unknown>;
  /** Receive an external payload (shelf / template / cancellation drawer). */
  onExternalDrop?: (drop: MCExternalDrop) => void | Promise<unknown>;
  /** "v" on a cell without a movable event — umziehen place step (bridge). */
  onEmptyCellV?: (cell: { resourceId: string; slotMin: number }) => boolean;
  /** Receives the imperative keyboard API (see MobiliCalendarKeyboardApi). */
  apiRef?: React.MutableRefObject<MobiliCalendarKeyboardApi | null>;
  onStats?: (stats: MCStats) => void;
  config?: Partial<MCGridConfig>;
  /**
   * Times the selected patient is unavailable, rendered as day-spanning
   * bands behind all columns (see model/patientAvailability.ts). Replaces
   * the legacy synthetic patient-availability column.
   */
  patientBlockedRanges?: ReadonlyArray<MCTimeRange>;
  /**
   * Text for the top-left corner cell. Defaults to the ISO calendar week of
   * `date`. Supplied by the caller rather than derived from a mode flag —
   * a weekly-template view (Dauertermin) wants the weekday here, and the
   * engine must stay ignorant of what view modes exist.
   */
  cornerLabel?: string;
  /**
   * Right-click on an event block. The engine only reports WHAT was clicked
   * and WHERE — menu content, actions and dialogs are the shell's business
   * (molecules/MobiliCalendar/actions). Also fired for Shift+F10-style
   * keyboard menu requests on the focused event (the browser sends those as
   * contextmenu events too).
   */
  onEventContextMenu?: (event: MCEvent, position: { x: number; y: number }) => void;
}

const JUMP_PULSE_MS = 3_400;
const JUMP_PENDING_TIMEOUT_MS = 8_000;
/**
 * Pixels of pointer travel before a press on an event becomes a drag.
 *
 * Measured as true distance, not |dx| + |dy|: the Manhattan sum crosses a
 * threshold of 5 after only ~3.5px of diagonal movement, which ordinary hand
 * jitter during a click clears easily. A click that accidentally armed a move
 * used to land the grid on the 5-minute cell under the cursor with no event
 * selected and the patient drawer deliberately suppressed — i.e. a plain
 * click appeared to select the cell behind the event and load nothing.
 */
const EVENT_DRAG_THRESHOLD_PX = 8;
/**
 * Safety net for pending continuity patches: normally retired by snapshot
 * match or commit settlement; this only catches a bridge that neither
 * returns a promise nor ever produces a matching snapshot.
 */
const PENDING_MOVE_TIMEOUT_MS = 30_000;
const EMPTY_PENDING: ReadonlyMap<string, MCPendingMove> = new Map();

const isoWeek = (d: Date): number => {
  const t = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const dayNum = t.getUTCDay() || 7;
  t.setUTCDate(t.getUTCDate() + 4 - dayNum);
  const yearStart = Date.UTC(t.getUTCFullYear(), 0, 1);
  return Math.ceil(((t.getTime() - yearStart) / 86400000 + 1) / 7);
};

const MobiliCalendar: React.FC<MobiliCalendarProps> = ({
  events,
  resources,
  date,
  isLoading = false,
  selectedPatientId,
  targetAppointmentId,
  onJumpTargetHandled,
  onEventSelect,
  onEventCursor,
  onMoveEvents,
  onExternalDrop,
  onEmptyCellV,
  apiRef,
  onStats,
  config,
  patientBlockedRanges,
  cornerLabel,
  onEventContextMenu,
}) => {
  const cfg = useMemo<MCGridConfig>(
    () => ({ ...DEFAULT_GRID_CONFIG, ...config }),
    [config],
  );

  // ── Pending continuity patches (commit → optimistic-patch gap) ─────────
  //
  // After a drop we keep rendering the moved events at their TARGET by
  // patching the incoming snapshot (see model/pendingMoves.ts). This replaces
  // the old calendar's whole-array freeze + awaiting-flush machinery: fresh
  // snapshots keep flowing at all times — only the just-moved slots are
  // overridden until the cache catches up or the commit settles/rolls back.
  const [pendingMoves, setPendingMoves] =
    useState<ReadonlyMap<string, MCPendingMove>>(EMPTY_PENDING);
  const patchedEvents = useMemo(
    () => applyPendingMoves(events, pendingMoves),
    [events, pendingMoves],
  );

  // ── Data indexes (pure, memoized) ────────────────────────────────────────
  const normalizedResources = useMemo(
    () => normalizeResources(resources, date, cfg),
    [resources, date, cfg],
  );
  const index: MCEventIndex = useMemo(
    () => buildEventIndex(patchedEvents, normalizedResources, cfg, date),
    [patchedEvents, normalizedResources, cfg, date],
  );
  const resourceIndexById = useMemo(() => {
    const map = new Map<string, number>();
    normalizedResources.forEach((r) => map.set(r.id, r.index));
    return map;
  }, [normalizedResources]);

  // ── Interaction store (external, render-decoupled) ───────────────────────
  const store = useMemo(
    () =>
      createMiniStore<MCInteractionState>({
        focus: null,
        focusedEventId: null,
        selectedEventId: null,
        range: null,
        multiSelectedIds: EMPTY_ID_SET,
        move: null,
        external: null,
      }),
    [],
  );

  const rootRef = useRef<HTMLDivElement>(null);
  const scrollerRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const colWidthRef = useRef<number>(cfg.minColumnWidthPx);
  const indexRef = useRef<MCEventIndex>(index);
  const resourcesRef = useRef(normalizedResources);
  indexRef.current = index;
  resourcesRef.current = normalizedResources;

  const selectEvent = useCallback(
    (event: MCEvent) => {
      const resourceIndex = resourceIndexById.get(event.resourceId);
      store.set({
        selectedEventId: event.id,
        focusedEventId: event.id,
        multiSelectedIds: new Set([event.id]),
        range: null,
        focus:
          resourceIndex != null
            ? { resourceIndex, slotMin: clampSlot(cfg, event.startMin) }
            : store.get().focus,
      });
      onEventSelect?.(event);
    },
    [cfg, store, resourceIndexById, onEventSelect],
  );

  // Commit wrapper: register the continuity patch in the SAME event-handler
  // tick as the move-state clear (React batches both into one commit — the
  // moved events render at their target with no old-position frame), then
  // hand the moves to the bridge and retire the patch when the commit
  // settles. Snapshot-match retirement (effect below) usually wins long
  // before — the moment the optimistic cache patch arrives.
  const onMoveEventsRef = useRef(onMoveEvents);
  onMoveEventsRef.current = onMoveEvents;

  const retirePending = useCallback((entries: ReadonlyArray<MCPendingMove>) => {
    setPendingMoves((prev) => {
      let next: Map<string, MCPendingMove> | null = null;
      for (const entry of entries) {
        // seq-guard: never retire a NEWER entry for the same slot (the user
        // may have re-dragged it while this commit was still in flight).
        if (prev.get(entry.key)?.seq === entry.seq) {
          if (!next) next = new Map(prev);
          next.delete(entry.key);
        }
      }
      return next ?? prev;
    });
  }, []);

  const handleMoveCommit = useCallback(
    (moves: MCEventMove[]) => {
      const batch = buildPendingMoves(moves);
      if (batch.length > 0) {
        setPendingMoves((prev) => {
          const next = new Map(prev);
          for (const entry of batch) next.set(entry.key, entry);
          return next;
        });
      }

      const result = onMoveEventsRef.current?.(moves);

      if (batch.length > 0) {
        // Safety timeout always; settle-retire only for promise-returning
        // bridges (a void bridge would otherwise retire instantly, before the
        // optimistic patch lands — recreating the old flash).
        const timer = setTimeout(() => retirePending(batch), PENDING_MOVE_TIMEOUT_MS);
        if (result && typeof (result as Promise<unknown>).then === "function") {
          (result as Promise<unknown>)
            .catch(() => {})
            .then(() => {
              clearTimeout(timer);
              retirePending(batch);
            });
        }
      }
    },
    [retirePending],
  );

  // Snapshot-match retirement: the moment the AUTHORITATIVE input already has
  // a moved slot at its target (optimistic patch / server reconcile), its
  // patch entry becomes a no-op — drop it so later authoritative changes
  // (socket updates from other users, rollbacks) flow through unhindered.
  useEffect(() => {
    if (pendingMoves.size === 0) return;
    const satisfied = satisfiedPendingKeys(events, pendingMoves);
    if (satisfied.length === 0) return;
    retirePending(
      satisfied
        .map((key) => pendingMoves.get(key))
        .filter((e): e is MCPendingMove => Boolean(e)),
    );
  }, [events, pendingMoves, retirePending]);

  const handleEmptyCellV = useCallback(
    (cell: { resourceIndex: number; slotMin: number }): boolean => {
      const resource = resourcesRef.current?.[cell.resourceIndex];
      if (!resource || !onEmptyCellV) return false;
      return onEmptyCellV({ resourceId: resource.id, slotMin: cell.slotMin });
    },
    [onEmptyCellV],
  );

  const { onKeyDown, applyRange, moveActions } = useGridKeyboard({
    cfg,
    store,
    indexRef,
    resourcesRef,
    scrollerRef,
    colWidthRef,
    onEventSelect,
    onEventCursor,
    onMoveCommit: handleMoveCommit,
    onEmptyCellV: handleEmptyCellV,
  });

  // Document-level key handling — parity with the old engine, whose cell
  // cursor (CellNavigation.ts) listens on document so the keyboard works
  // without the grid holding DOM focus. The hook guards typing contexts; open
  // dialogs own the keyboard entirely (same guard as the old calendar).
  useEffect(() => {
    const listener = (e: KeyboardEvent) => {
      if (isModalOrDialogOpen()) return;
      onKeyDown(e);
    };
    document.addEventListener("keydown", listener);
    return () => document.removeEventListener("keydown", listener);
  }, [onKeyDown]);

  // Imperative keyboard API for the bridge's provider-registered hotkeys.
  useEffect(() => {
    if (!apiRef) return;
    apiRef.current = {
      getFocusedCell: () => {
        const focus = store.get().focus;
        const resource = focus ? resourcesRef.current?.[focus.resourceIndex] : null;
        return focus && resource ? { resourceId: resource.id, slotMin: focus.slotMin } : null;
      },
      getFocusedEvent: () => {
        const id = store.get().focusedEventId;
        return id ? (indexRef.current?.byId.get(id) ?? null) : null;
      },
    };
    return () => {
      apiRef.current = null;
    };
  }, [apiRef, store]);

  // ── Column width ← container size (CSS var only, no re-render) ───────────
  // This measures the scroller and writes a var the canvas width derives from
  // (.mc-canvas: gutter + count * --mc-col-w), so the write can feed straight
  // back into the observed box: a wider canvas toggles the `overflow: auto`
  // scrollbars, which changes clientWidth again. Two guards keep that from
  // becoming a loop — writing only on an actual change so the feedback
  // converges after one pass, and never writing from inside the observer
  // callback (a rAF hop) so a resize burst can't leave notifications
  // undelivered for the frame.
  useLayoutEffect(() => {
    const scroller = scrollerRef.current;
    const root = rootRef.current;
    if (!scroller || !root) return;

    let frame = 0;

    const apply = () => {
      const available = scroller.clientWidth - cfg.gutterWidthPx;
      const n = Math.max(normalizedResources.length, 1);
      const width = Math.max(
        cfg.minColumnWidthPx,
        Math.min(cfg.maxColumnWidthPx, Math.floor(available / n)),
      );
      if (width === colWidthRef.current) return;
      colWidthRef.current = width;
      root.style.setProperty("--mc-col-w", `${width}px`);
    };

    // Initial pass stays synchronous — deferring it would paint one frame at
    // the CSS default column width first.
    colWidthRef.current = 0;
    apply();

    const observer = new ResizeObserver(() => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(apply);
    });
    observer.observe(scroller);
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, [cfg, normalizedResources.length]);

  // ── Scroll shadows (class toggles via ref, no state) ─────────────────────
  useEffect(() => {
    const scroller = scrollerRef.current;
    const root = rootRef.current;
    if (!scroller || !root) return;
    let scrolledX = false;
    let scrolledY = false;
    const onScroll = () => {
      const x = scroller.scrollLeft > 1;
      const y = scroller.scrollTop > 1;
      if (x !== scrolledX) {
        scrolledX = x;
        root.classList.toggle("is-scrolled-x", x);
      }
      if (y !== scrolledY) {
        scrolledY = y;
        root.classList.toggle("is-scrolled-y", y);
      }
    };
    scroller.addEventListener("scroll", onScroll, { passive: true });
    return () => scroller.removeEventListener("scroll", onScroll);
  }, []);

  // ── Initial vertical position: ~1h before now (today) or 08:00 ───────────
  const dateKey = date.toDateString();
  useLayoutEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    const now = new Date();
    const anchorMin =
      now.toDateString() === dateKey
        ? clampSlot(cfg, now.getHours() * 60 + now.getMinutes() - 60)
        : 8 * 60;
    scroller.scrollTop = Math.max(0, yForMin(cfg, clampSlot(cfg, anchorMin)) - 8);
  }, [cfg, dateKey]);

  // ── Pointer: delegation on the body (single listener for all events) ─────
  const marqueeRef = useRef<{
    startCell: { resourceIndex: number; slotMin: number };
    startX: number;
    startY: number;
    active: boolean;
  } | null>(null);
  /**
   * Armed on pointer-down over a movable event; becomes a move TRANSACTION
   * (pickUp → previewAtCell → place) once the pointer travels past the
   * threshold. A press that never crosses it stays a plain click/select.
   */
  const eventDragRef = useRef<{
    eventId: string;
    startX: number;
    startY: number;
    grabCell: { resourceIndex: number; slotMin: number };
    active: boolean;
  } | null>(null);
  const suppressClickRef = useRef(false);
  const pointerRafRef = useRef(0);

  const cellFromClient = useCallback(
    (clientX: number, clientY: number) => {
      const body = bodyRef.current;
      if (!body) return null;
      const rect = body.getBoundingClientRect();
      return cellForPoint(
        cfg,
        resourcesRef.current?.length ?? 0,
        colWidthRef.current || 1,
        clientX - rect.left,
        clientY - rect.top,
      );
    },
    [cfg],
  );

  /** Auto-scroll when dragging near the scroller edges (marquee & move). */
  const edgeAutoScroll = useCallback(
    (clientX: number, clientY: number) => {
      const scroller = scrollerRef.current;
      if (!scroller) return;
      const rect = scroller.getBoundingClientRect();
      const margin = 36;
      let dx = 0;
      let dy = 0;
      if (clientY < rect.top + cfg.headerHeightPx + margin) dy = -14;
      else if (clientY > rect.bottom - margin) dy = 14;
      if (clientX < rect.left + cfg.gutterWidthPx + margin) dx = -14;
      else if (clientX > rect.right - margin) dx = 14;
      if (dx || dy) scroller.scrollBy({ left: dx, top: dy });
    },
    [cfg],
  );

  const handlePointerDown = useCallback(
    (e: React.PointerEvent) => {
      scrollerRef.current?.focus({ preventScroll: true });
      if (e.button !== 0) return;
      if (store.get().move) return; // move mode: click places (see click handler)

      const target = e.target as HTMLElement;
      if (e.shiftKey || e.metaKey || e.ctrlKey) return; // modifier = selection ops

      const eventEl = target.closest<HTMLElement>("[data-event-id]");
      if (eventEl) {
        // Arm a potential single-event (or selection) drag; presses that never
        // cross the threshold keep their click/select semantics untouched.
        const event = indexRef.current?.byId.get(eventEl.dataset.eventId ?? "");
        if (!event || !isMovableEvent(event)) return;
        const cell = cellFromClient(e.clientX, e.clientY);
        if (!cell) return;
        eventDragRef.current = {
          eventId: event.id,
          startX: e.clientX,
          startY: e.clientY,
          grabCell: cell,
          active: false,
        };
        (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
        return;
      }

      const cell = cellFromClient(e.clientX, e.clientY);
      if (!cell) return;
      marqueeRef.current = {
        startCell: cell,
        startX: e.clientX,
        startY: e.clientY,
        active: false,
      };
      (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    },
    [store, cellFromClient],
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent) => {
      const { clientX, clientY } = e;

      // ── Event drag: activate the move transaction past the threshold ────
      const eventDrag = eventDragRef.current;
      if (eventDrag && !eventDrag.active && !store.get().move) {
        const moved = Math.hypot(
          clientX - eventDrag.startX,
          clientY - eventDrag.startY,
        );
        if (moved >= EVENT_DRAG_THRESHOLD_PX) {
          const state = store.get();
          // Dragging a member of a multi-selection moves the WHOLE selection
          // (Excel semantics, like the grouping bars); otherwise the drag
          // narrows the working set to the pressed event. Selection is set
          // WITHOUT onEventSelect — a drag must not thrash the patient drawer.
          const dragWholeSelection =
            state.multiSelectedIds.size > 1 && state.multiSelectedIds.has(eventDrag.eventId);
          store.set({
            multiSelectedIds: dragWholeSelection
              ? state.multiSelectedIds
              : new Set([eventDrag.eventId]),
            range: null,
            focusedEventId: null,
            focus: eventDrag.grabCell,
          });
          if (moveActions.pickUp()) {
            eventDrag.active = true;
            suppressClickRef.current = true;
          } else {
            eventDragRef.current = null;
          }
        }
      }

      // ── Move mode: ghost follows the pointer ───────────────────────────
      if (store.get().move) {
        if (pointerRafRef.current) return;
        pointerRafRef.current = requestAnimationFrame(() => {
          pointerRafRef.current = 0;
          const cell = cellFromClient(clientX, clientY);
          if (cell) moveActions.previewAtCell(cell);
          edgeAutoScroll(clientX, clientY);
        });
        return;
      }

      // ── Marquee range selection ────────────────────────────────────────
      const marquee = marqueeRef.current;
      if (!marquee) return;
      if (!marquee.active) {
        const moved =
          Math.abs(clientX - marquee.startX) + Math.abs(clientY - marquee.startY);
        if (moved < 5) return;
        marquee.active = true;
        suppressClickRef.current = true;
      }
      if (pointerRafRef.current) return;
      pointerRafRef.current = requestAnimationFrame(() => {
        pointerRafRef.current = 0;
        const active = marqueeRef.current;
        if (!active?.active) return;
        const cell = cellFromClient(clientX, clientY);
        if (cell) applyRange(active.startCell, cell);
        edgeAutoScroll(clientX, clientY);
      });
    },
    [store, cellFromClient, moveActions, applyRange, edgeAutoScroll],
  );

  const handlePointerUp = useCallback(
    (e: React.PointerEvent) => {
      const eventDrag = eventDragRef.current;
      if (eventDrag) {
        eventDragRef.current = null;
        (e.currentTarget as HTMLElement).releasePointerCapture?.(e.pointerId);
        if (eventDrag.active) {
          // A drag that ends on the cell it started from is a CLICK the user
          // happened to wobble through — never a move. Committing it would
          // either be a silent no-op or, if the wobble crossed a slot edge, a
          // real 5-minute reschedule nobody asked for. Cancel and fall back to
          // normal click semantics so the event gets selected and the patient
          // drawer opens (the drag path suppresses both by design).
          const releaseCell = cellFromClient(e.clientX, e.clientY);
          const sameCell =
            !!releaseCell &&
            releaseCell.resourceIndex === eventDrag.grabCell.resourceIndex &&
            releaseCell.slotMin === eventDrag.grabCell.slotMin;
          if (sameCell) {
            moveActions.cancel();
            const event = indexRef.current?.byId.get(eventDrag.eventId);
            if (event) selectEvent(event);
            suppressClickRef.current = false;
            return;
          }
          // Releasing outside the calendar aborts (drag-away-to-cancel);
          // inside, an invalid target snaps back — the ghost already showed
          // the red tint, and no mutation handler is ever invoked.
          const rect = scrollerRef.current?.getBoundingClientRect();
          const inside =
            !!rect &&
            e.clientX >= rect.left &&
            e.clientX < rect.right &&
            e.clientY >= rect.top &&
            e.clientY < rect.bottom;
          if (!inside || !moveActions.place()) moveActions.cancel();
        }
        return;
      }
      if (marqueeRef.current) {
        (e.currentTarget as HTMLElement).releasePointerCapture?.(e.pointerId);
        marqueeRef.current = null;
      }
    },
    [moveActions, cellFromClient, selectEvent],
  );

  const handlePointerCancel = useCallback(
    (e: React.PointerEvent) => {
      const eventDrag = eventDragRef.current;
      if (eventDrag) {
        eventDragRef.current = null;
        (e.currentTarget as HTMLElement).releasePointerCapture?.(e.pointerId);
        if (eventDrag.active) moveActions.cancel();
        return;
      }
      if (marqueeRef.current) {
        (e.currentTarget as HTMLElement).releasePointerCapture?.(e.pointerId);
        marqueeRef.current = null;
      }
    },
    [moveActions],
  );

  const handleBodyContextMenu = useCallback(
    (e: React.MouseEvent) => {
      if (!onEventContextMenu) return; // no consumer: keep the browser menu
      const eventEl = (e.target as HTMLElement).closest<HTMLElement>("[data-event-id]");
      if (!eventEl) return; // empty cell: browser menu for now
      const event = indexRef.current?.byId.get(eventEl.dataset.eventId ?? "");
      if (!event) return;
      e.preventDefault();
      // Right-click also SELECTS (ring + focus), like every desktop calendar —
      // but deliberately without onEventSelect: opening a menu must not thrash
      // the patient drawer the way an explicit click does.
      const resourceIndex = resourceIndexById.get(event.resourceId);
      store.set({
        selectedEventId: event.id,
        focusedEventId: event.id,
        multiSelectedIds: new Set([event.id]),
        range: null,
        focus:
          resourceIndex != null
            ? { resourceIndex, slotMin: clampSlot(cfg, event.startMin) }
            : store.get().focus,
      });
      onEventContextMenu(event, { x: e.clientX, y: e.clientY });
    },
    [onEventContextMenu, store, resourceIndexById, cfg],
  );

  const handleBodyClick = useCallback(
    (e: React.MouseEvent) => {
      // A finished marquee drag must not be re-interpreted as a click.
      if (suppressClickRef.current) {
        suppressClickRef.current = false;
        return;
      }

      // ── Move mode: any grid click places at the pointer ────────────────
      if (store.get().move) {
        const cell = cellFromClient(e.clientX, e.clientY);
        if (cell) moveActions.previewAtCell(cell);
        moveActions.place();
        return;
      }

      const target = e.target as HTMLElement;
      const eventEl = target.closest<HTMLElement>("[data-event-id]");
      if (eventEl) {
        const event = index.byId.get(eventEl.dataset.eventId ?? "");
        if (!event) return;

        // Ctrl/Cmd+Click XORs the event into the multi-selection (Excel).
        if (e.metaKey || e.ctrlKey) {
          const prev = store.get().multiSelectedIds;
          const next = new Set(prev);
          if (next.has(event.id)) next.delete(event.id);
          else next.add(event.id);
          store.set({ multiSelectedIds: next, range: null });
          return;
        }
        selectEvent(event);
        return;
      }

      const cell = cellFromClient(e.clientX, e.clientY);
      if (!cell) return;

      // Shift+Click extends a range from the current anchor (Excel).
      if (e.shiftKey) {
        const state = store.get();
        const anchor = state.range?.anchor ?? state.focus ?? cell;
        applyRange(anchor, cell);
        return;
      }

      store.set({
        focus: cell,
        focusedEventId: null,
        selectedEventId: null,
        range: null,
        multiSelectedIds: EMPTY_ID_SET,
      });
    },
    [store, index, cellFromClient, moveActions, applyRange, selectEvent],
  );

  // Root class while moving / external-dragging (cursor + hover suppression),
  // no re-render.
  useEffect(() => {
    return store.subscribe(() => {
      const state = store.get();
      rootRef.current?.classList.toggle("is-moving", Boolean(state.move));
      rootRef.current?.classList.toggle(
        "is-ext-drag",
        Boolean(state.external && !state.external.settling),
      );
    });
  }, [store]);

  // ── External drags (shelf / template / cancellation-drawer payloads) ─────
  const dateRef = useRef(date);
  dateRef.current = date;
  useExternalDrag({
    cfg,
    store,
    indexRef,
    resourcesRef,
    rootRef,
    bodyRef,
    dateRef,
    cellFromClient,
    edgeAutoScroll,
    onDrop: onExternalDrop,
  });

  // Settling-ghost fast path: clear the post-drop external placeholder the
  // moment the receive's OPTIMISTIC event is present in the index AT THE DROP
  // POSITION (the fallback settle/timeout clearing lives in useExternalDrag).
  // Position-aware on purpose: a pinboard "scheduled slot move" payload's
  // slot still exists in the grid at its OLD position — mere presence must
  // not clear the ghost. Templates are excluded — their appointment id does
  // not exist until the server creates it, so they clear on settle.
  useEffect(() => {
    const external = store.get().external;
    if (!external?.settling || external.payload.isTemplate) return;
    const targetResourceId = normalizedResources[external.resourceIndex]?.id;
    if (!targetResourceId) return;
    const atTarget = (event: MCEvent | undefined): boolean =>
      !!event &&
      event.resourceId === targetResourceId &&
      event.startMin === external.startMin;

    const appointmentId = external.payload.id;
    const slotId = external.payload.timeSlots?.[0]?.id;
    const matched =
      (appointmentId &&
        (index.byAppointmentId.get(appointmentId) ?? []).some((id) =>
          atTarget(index.byId.get(id)),
        )) ||
      (slotId &&
        index.events.some(
          (e) => e.raw.extendedProps?.timeSlotId === slotId && atTarget(e),
        ));
    if (matched) store.set({ external: null });
  }, [index, normalizedResources, store]);

  // ── Tier 3: declarative jump target (pending until data contains it) ─────
  const [jumpEventIds, setJumpEventIds] = useState<ReadonlySet<string> | null>(null);
  useEffect(() => {
    if (!targetAppointmentId) {
      setJumpEventIds(null);
      return;
    }
    const ids = index.byAppointmentId.get(targetAppointmentId);
    const events = (ids ?? [])
      .map((id) => index.byId.get(id))
      .filter((e): e is MCEvent => Boolean(e));
    if (events.length === 0) {
      // Data not there yet — stay pending, but give up eventually.
      const giveUp = setTimeout(() => onJumpTargetHandled?.(), JUMP_PENDING_TIMEOUT_MS);
      return () => clearTimeout(giveUp);
    }
    const primary = events.reduce((a, b) => (b.startMin < a.startMin ? b : a));
    const resourceIndex = resourceIndexById.get(primary.resourceId) ?? 0;
    const scroller = scrollerRef.current;
    if (scroller) {
      const colWidth = colWidthRef.current || 1;
      scroller.scrollTo({
        left: Math.max(
          0,
          cfg.gutterWidthPx + resourceIndex * colWidth - scroller.clientWidth / 2 + colWidth / 2,
        ),
        top: Math.max(
          0,
          cfg.headerHeightPx +
            yForMin(cfg, primary.startMin) -
            scroller.clientHeight / 2,
        ),
        behavior: "smooth",
      });
    }
    setJumpEventIds(new Set(events.map((e) => e.id)));
    store.set({
      focus: { resourceIndex, slotMin: clampSlot(cfg, primary.startMin) },
      focusedEventId: primary.id,
    });
    const done = setTimeout(() => {
      setJumpEventIds(null);
      onJumpTargetHandled?.();
    }, JUMP_PULSE_MS);
    return () => clearTimeout(done);
  }, [targetAppointmentId, index, cfg, store, resourceIndexById, onJumpTargetHandled]);

  const jumpIdsByResource = useMemo(() => {
    if (!jumpEventIds) return undefined;
    const map = new Map<string, Set<string>>();
    jumpEventIds.forEach((id) => {
      const event = index.byId.get(id);
      if (!event) return;
      let set = map.get(event.resourceId);
      if (!set) map.set(event.resourceId, (set = new Set()));
      set.add(id);
    });
    return map;
  }, [jumpEventIds, index]);

  // ── Tier 1: selected-patient event ids per column ────────────────────────
  const patientTierByResource = useMemo(() => {
    if (selectedPatientId == null) return undefined;
    const ids = index.byPatientId.get(selectedPatientId);
    if (!ids || ids.length === 0) return undefined;
    const map = new Map<string, Set<string>>();
    for (const id of ids) {
      const event = index.byId.get(id);
      if (!event) continue;
      let set = map.get(event.resourceId);
      if (!set) map.set(event.resourceId, (set = new Set()));
      set.add(event.id);
    }
    return map;
  }, [index, selectedPatientId]);

  // ── Perf HUD stats ────────────────────────────────────────────────────────
  useEffect(() => {
    onStats?.({
      eventCount: index.events.length,
      backgroundCount: index.background.length,
      resourceCount: normalizedResources.length,
      indexBuildMs: index.buildMs,
    });
  }, [index, normalizedResources.length, onStats]);

  const cssVars = {
    "--mc-col-count": normalizedResources.length || 1,
    "--mc-slot-count": slotCount(cfg),
    "--mc-slot-h": `${cfg.slotHeightPx}px`,
    "--mc-gutter-w": `${cfg.gutterWidthPx}px`,
    "--mc-header-h": `${cfg.headerHeightPx}px`,
  } as React.CSSProperties;

  return (
    <div ref={rootRef} className="mc-root" style={cssVars}>
      {isLoading && <div className="mc-loading" />}
      <div
        ref={scrollerRef}
        className="mc-scroller"
        tabIndex={0}
        role="grid"
        aria-label="MobiliCalendar Terminplan"
      >
        <div className="mc-canvas">
          <div className="mc-header">
            <div className="mc-corner">{cornerLabel ?? `KW ${isoWeek(date)}`}</div>
            {normalizedResources.map((resource) => (
              <HeaderCell key={resource.id} resource={resource} store={store} />
            ))}
          </div>
          <div className="mc-under">
            <div className="mc-gutter">
              <TimeGutter cfg={cfg} store={store} />
            </div>
            <div
              ref={bodyRef}
              className="mc-body"
              onClick={handleBodyClick}
              onContextMenu={handleBodyContextMenu}
              onPointerDown={handlePointerDown}
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerUp}
              onPointerCancel={handlePointerCancel}
            >
              {normalizedResources.map((resource) => (
                <ResourceColumn
                  key={resource.id}
                  resource={resource}
                  events={eventsForResource(index, resource.id)}
                  background={backgroundForResource(index, resource.id)}
                  cfg={cfg}
                  tierPatientIds={patientTierByResource?.get(resource.id)}
                  jumpEventIds={jumpIdsByResource?.get(resource.id)}
                />
              ))}
              {/* After the columns on purpose: the band washes over the event
                  blocks. Before GroupingBars so the (interactive) bars stay
                  legible on top of it — both sit at z-index 5, so DOM order
                  decides. */}
              {patientBlockedRanges && patientBlockedRanges.length > 0 && (
                <PatientBlockedBand cfg={cfg} ranges={patientBlockedRanges} />
              )}
              {isGroupingBarsEnabled() && (
                <GroupingBars
                  cfg={cfg}
                  index={index}
                  resources={normalizedResources}
                  resourceIndexById={resourceIndexById}
                  store={store}
                  moveActions={moveActions}
                  cellFromClient={cellFromClient}
                />
              )}
              <SelectionOverlay
                cfg={cfg}
                store={store}
                index={index}
                resources={normalizedResources}
                resourceIndexById={resourceIndexById}
              />
              <ExternalDragOverlay cfg={cfg} store={store} />
              <FocusOverlay
                cfg={cfg}
                store={store}
                index={index}
                resourceIndexById={resourceIndexById}
              />
              <NowLine cfg={cfg} date={date} />
            </div>
          </div>
          {normalizedResources.length === 0 && !isLoading && (
            <div className="mc-empty">Keine Therapeuten für diesen Tag</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default React.memo(MobiliCalendar);
